import React, { useState, useEffect } from 'react';
import { X, ArrowUpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Item } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface RestockModalProps {
  item: Item | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RestockModal: React.FC<RestockModalProps> = ({
  item,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    quantity: 1,
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      setFormData({
        quantity: 1,
        notes: `Wareneingang - ${item.name}`,
      });
    }
  }, [isOpen, item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item) return;
    
    if (formData.quantity <= 0) {
      toast.error('Menge muss größer als 0 sein');
      return;
    }

    setLoading(true);
    try {
      // Erstelle eine CHECKIN Transaction für den Wareneingang
      await apiService.post('/transactions', {
        itemId: item.id,
        type: 'CHECKIN',
        quantity: formData.quantity,
        notes: formData.notes || 'Admin Wareneingang',
      });

      toast.success('Wareneingang erfolgreich verbucht');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error processing restock:', error);
      toast.error('Fehler beim Wareneingang');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-xl mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-green-600" />
            Wareneingang
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {/* Item Info */}
          <div className="bg-muted p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-lg mb-2">{item.name}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Mat. Nr:</span> {item.sku || '-'}
              </div>
              <div>
                <span className="text-muted-foreground">Aktueller Bestand:</span> {item.quantity}
              </div>
              <div>
                <span className="text-muted-foreground">Standort:</span> {item.location?.name || '-'}
              </div>
              <div>
                <span className="text-muted-foreground">Art:</span>
                <Badge variant={item.isRental ? "default" : "secondary"} className="ml-2">
                  {item.isRental ? 'Mietgerät' : 'Verbrauchsmaterial'}
                </Badge>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Menge für Wareneingang *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 1)}
                placeholder="Anzahl der hinzuzufügenden Items"
                required
              />
              <p className="text-xs text-muted-foreground">
                Neuer Bestand nach Eingang: {item.quantity + formData.quantity}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Grund für Wareneingang..."
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                disabled={loading || formData.quantity <= 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Wareneingang wird verbucht...' : 'Wareneingang verbuchen'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RestockModal;
